import * as Papa from 'papaparse';
import { useRef, useEffect, useCallback } from 'react';

export const capitalizeFirstLetter = (s: string) => {
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export const titleCase = (s: string) =>
  // Convert kebab/snake case to title case
  s
    .replace(/^[-_]*(.)/, (_, c) => c.toUpperCase()) // Initial char (after -/_)
    .replace(/[-_]+(.)/g, (_, c) => ' ' + c.toUpperCase()); // First char after each -/_

export const delayTyping = (() => {
  let timer = 0;
  return (callback: () => void, ms: number) => {
    clearTimeout(timer);
    timer = window.setTimeout(callback, ms);
  };
})();

export const exportToCsv = (
  filename: string,
  rows: object[],
  headers: string[]
): void => {
  if (!rows || !rows.length) {
    return;
  }

  const csv = Papa.unparse({
    data: rows,
    fields: headers,
  });
  const blob = new Blob([csv]);
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const parseCsvFileToArray = async (file: File) => {
  if (file && file.type !== 'text/csv') {
    alert('Unable to process the file, please upload only CSV file');
    return false;
  }

  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      skipEmptyLines: true,
      complete: (results) => {
        resolve((results.data as Array<string>).flat());
      },
      error: () => {
        alert('Unable to process the CSV file');
        reject('Error');
      },
    });
  });
};

export const convertFlatCsvArrayToObjects = (
  flat: string[],
  fields: string[]
) => {
  const values = flat.slice(fields.length);

  return Array.from(
    { length: values.length / fields.length },
    (_, rowIndex) => {
      const start = rowIndex * fields.length;
      const slice = values.slice(start, start + fields.length);

      return Object.fromEntries(
        fields.map((field, colIndex) => [field, slice[colIndex] ?? ''])
      );
    }
  );
};

export const removeUnderscores = (str: string): string => {
  return str
    .split('_')
    .filter((word) => word)
    .join(' ');
};

export const hideParentLayout = (path: string, arr: string[]): boolean => {
  return arr.some((a) => path.includes(a));
};

export default function useTimeout(callback: () => void, delay: number) {
  const callbackRef = useRef(callback);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const set = useCallback(() => {
    timeoutRef.current = setTimeout(() => callbackRef.current(), delay);
  }, [delay]);

  const clear = useCallback(() => {
    timeoutRef.current && clearTimeout(timeoutRef.current);
  }, []);

  useEffect(() => {
    set();
    return clear;
  }, [delay, set, clear]);

  const reset = useCallback(() => {
    clear();
    set();
  }, [clear, set]);

  return { reset, clear };
}
